// Finance-first Dashboard hero (Aug 2026 client request).
//
// Rendered directly above OperationsCommandCenter. Answers the money
// questions the client wants admins to see FIRST:
//   - Billed Today, Paid Today, Invoiced This Month, Outstanding, Collection Rate
//   - Revenue by Location (admin all-locations view)
//   - Recent Payments (top 10)
//   - Outstanding Invoices (top 10)
//
// Location filter mirrors OperationsCommandCenter (own selector,
// scoped server-side by Invoice_View + DataScopeHelper). If the user
// lacks Invoice_View, the endpoint returns 403 and this component
// renders NOTHING — nobody without finance permission ever sees a
// financial value, per spec §25/§29.
//
// Never fabricates revenue: every value is a direct sum on the
// backend's canonical invoice/payment queries. No per-class-occurrence
// attribution surface (spec §9 — attribution isn't possible from the
// existing schema, so we don't approximate it).

import React from "react";
import {
  Box, Typography, Chip, Select, MenuItem, FormControl,
  alpha, CircularProgress, Divider, Stack, IconButton, Button,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PaymentsIcon from "@mui/icons-material/Payments";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useGetDashboardFinanceSummaryQuery } from "../../Api/attoDeskApi";
import { formatCurrency, formatCurrencyCompact } from "../../utils/currency";
import { DashboardTintedCard, DashboardTintedSkeleton } from "./DashboardTintedCard";
import { dashTokens, toneMap, toneForLocationIndex } from "./dashboardTheme";

// Muted premium palette (2026-08-24). Dark forest green is REMOVED as
// a brand color; `deepGreen` here is repointed to steel blue so every
// legacy consumer in this file (header, borders, chevrons, buttons)
// picks up the new identity in one edit. Semantic green stays only as
// `green` for success/paid states.
const c = {
  navy: "#17324D", deepGreen: "#4E78A6", green: "#5BAA83",
  amber: "#B4934B", red: "#BF6973", blue: "#4E78A6",
  text: "#17324D", muted: "#7D8FA1", border: "#E2E8F0", card: "#FFFFFF",
  surfaceMint: "#F3F8F5", surfaceBlue: "#F2F6FA",
  surfaceAmber: "#FBF7ED", surfaceRed: "#FCF3F4",
  surfaceGrey: "#F3F5F8", surfaceNavy: "#EEF2F7",
  surfacePurple: "#F5F6FB", purple: "#6878A6",
};

interface Props {
  /** Shared dashboard location filter — "all" = all locations. */
  locationId: number | "all";
  /** Called when the finance selector or a location card is clicked.
      Same handler used by OperationsCommandCenter so the dashboard has
      exactly one source of truth for the active location. */
  onLocationChange: (id: number | "all") => void;
}

const FinanceOverview: React.FC<Props> = ({ locationId, onLocationChange }) => {
  const navigate = useNavigate();
  const setLocationId = onLocationChange;
  const { data, isLoading, isFetching, error } = useGetDashboardFinanceSummaryQuery(
    { location_id: locationId === "all" ? null : locationId },
    { pollingInterval: 120_000 },  // refresh every 2 min
  );
  const d: any = data?.data || null;

  // If the endpoint returns 403 (no finance permission) render nothing.
  // Server is the authoritative gate; no data means no surface.
  const status = (error as any)?.status || (data as any)?.code;
  if (status === 403) return null;

  const isAdmin: boolean = !!d?.scope?.is_admin;
  const availableLocations: { id: number; name: string }[] = d?.available_locations || [];
  const summary = d?.summary || {};
  const locationRevenue: any[] = d?.location_revenue || [];
  const recentPayments: any[] = d?.recent_payments || [];
  const outstandingInvoices: any[] = d?.outstanding_invoices || [];

  const kpi = (
    label: string, value: React.ReactNode, hint: React.ReactNode,
    icon: React.ReactNode, tone: "green" | "blue" | "navy" | "amber" | "red" | "purple",
    tooltip?: string,
  ) => {
    const color = tone === "green" ? c.green
      : tone === "amber" ? c.amber
      : tone === "red" ? c.red
      : tone === "blue" ? c.blue
      : tone === "purple" ? c.purple
      : c.navy;
    const surface = tone === "green" ? c.surfaceMint
      : tone === "amber" ? c.surfaceAmber
      : tone === "red" ? c.surfaceRed
      : tone === "blue" ? c.surfaceBlue
      : tone === "purple" ? c.surfacePurple
      : c.surfaceNavy;
    return (
      <Box sx={{
        bgcolor: c.card, border: `1px solid ${c.border}`, borderRadius: "14px",
        boxShadow: "0 1px 2px rgba(15,30,51,0.03), 0 4px 14px rgba(15,30,51,0.04)",
        p: 2, minWidth: 0,
        transition: "all 180ms",
        "&:hover": { transform: "translateY(-2px)", boxShadow: "0 2px 4px rgba(15,30,51,0.05), 0 8px 22px rgba(15,30,51,0.07)" },
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: surface, color, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 18 } }}>
            {icon}
          </Box>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: c.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {label}
          </Typography>
        </Box>
        <Tooltip title={tooltip || ""} placement="top" arrow>
          <Typography sx={{ fontSize: 26, fontWeight: 800, color: c.text, lineHeight: 1.1, letterSpacing: "-0.02em" }}>
            {value}
          </Typography>
        </Tooltip>
        {hint && <Typography sx={{ fontSize: 11.5, color: c.muted, mt: 0.5 }}>{hint}</Typography>}
      </Box>
    );
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* ══════════ Finance Header ══════════ */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 2, mb: 2, flexWrap: "wrap",
      }}>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: c.muted, textTransform: "uppercase", letterSpacing: "0.1em" }}>
            Finance · {dayjs().format("MMMM YYYY")}
          </Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 800, color: c.text, mt: 0.25, letterSpacing: "-0.02em" }}>
            Financial Overview
          </Typography>
        </Box>
        {/* Global location selector (Aug 2026 polish) — eyebrow
            label + soft mint pill so this feels like the important
            dashboard-wide control it is. Owns the ONLY interactive
            location dropdown on the page. */}
        <Box>
          <Typography sx={{
            fontSize: 10, fontWeight: 700, color: c.muted,
            textTransform: "uppercase", letterSpacing: "0.1em", mb: 0.5, textAlign: "right",
          }}>
            Viewing Location
          </Typography>
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1,
            // ONE pill: this Box owns background + border + hover +
            // focus ring. Everything inside is transparent so no
            // second grey layer bleeds through.
            background: `#F2F6FA`,
            border: `1px solid #D5E1EA`,
            borderRadius: "12px", pl: 1.5, pr: 0.75, minHeight: 40,
            boxShadow: "0 2px 8px rgba(23,50,77,0.04)",
            transition: "all 180ms",
            "&:hover": {
              background: `#EAF2F8`,
              borderColor: c.deepGreen,
              boxShadow: "0 4px 12px rgba(23,50,77,0.08)",
            },
            "&:focus-within": {
              borderColor: c.deepGreen,
              boxShadow: "0 0 0 3px rgba(78,120,166,0.18)",
            },
          }}>
            <LocationOnIcon sx={{ fontSize: 18, color: c.deepGreen }} />
            {/* MUI standard-variant Select ships its own
                `.MuiInputBase-input` and `.MuiSelect-select` layers
                that both paint a subtle grey backdrop (visible on
                focus/hover). Since the outer <Box> already owns the
                pill's background, border, hover, focus-ring, and radius,
                force every Select internal layer transparent and flat
                so we end up with ONE clean surface — no double
                background, no double border, no grey rectangle. */}
            <FormControl size="small" variant="standard" sx={{ minWidth: 180, flex: 1 }}>
              <Select
                value={locationId === "all" ? "all" : String(locationId)}
                onChange={(e) => setLocationId(e.target.value === "all" ? "all" : Number(e.target.value))}
                variant="standard" disableUnderline
                sx={{
                  color: c.deepGreen, fontSize: 14, fontWeight: 800,
                  bgcolor: "transparent",
                  "& .MuiSelect-icon": { color: c.deepGreen, right: 4 },
                  "& .MuiSelect-select": {
                    background: "transparent !important",
                    py: 0.5, pl: 0, pr: "24px !important",
                    minHeight: "auto !important",
                    "&:focus": { background: "transparent" },
                  },
                  "& .MuiInputBase-input": {
                    background: "transparent",
                    padding: 0,
                  },
                  "&:before, &:after": { display: "none" },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      mt: 0.75, borderRadius: "12px",
                      boxShadow: "0 12px 32px rgba(15,30,51,0.14)",
                      border: `1px solid ${c.border}`,
                    },
                  },
                }}
              >
                {isAdmin && (
                  <MenuItem value="all" sx={{
                    fontSize: 13.5, fontWeight: 700,
                    "&.Mui-selected": { bgcolor: "#F2F6FA", color: c.deepGreen, "&:hover": { bgcolor: "#EAF2F8" } },
                  }}>All Locations</MenuItem>
                )}
                {availableLocations.map((l) => (
                  <MenuItem key={l.id} value={String(l.id)} sx={{
                    fontSize: 13.5, fontWeight: 700,
                    "&.Mui-selected": { bgcolor: "#F2F6FA", color: c.deepGreen, "&:hover": { bgcolor: "#EAF2F8" } },
                  }}>{l.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {isFetching && <CircularProgress size={12} sx={{ color: c.deepGreen, ml: 0.5 }} />}
          </Box>
        </Box>
      </Box>

      {/* ══════════ Financial KPI Row — 5 tinted cards per spec ══════════
          Each card owns its own tone: blue / green / purple / amber /
          teal. Loading state renders a color-matched shimmer inside
          the tinted surface (no flash to $0). */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" }, gap: 1.5, mb: 2.5 }}>
        <DashboardTintedCard
          tone="blue" icon={<ReceiptLongIcon />}
          label="Billed Today"
          value={isLoading ? <DashboardTintedSkeleton tone="blue" /> : formatCurrency(summary.billed_today)}
          hint="Invoices issued today"
        />
        <DashboardTintedCard
          tone="green" icon={<PaymentsIcon />}
          label="Paid Today"
          value={isLoading ? <DashboardTintedSkeleton tone="green" /> : formatCurrency(summary.paid_today)}
          hint="Completed payments today"
        />
        <DashboardTintedCard
          tone="purple" icon={<AttachMoneyIcon />}
          label="Invoiced This Month"
          value={
            <Tooltip title={formatCurrency(summary.invoiced_month)} placement="top" arrow>
              <span>{isLoading ? <DashboardTintedSkeleton tone="purple" /> : formatCurrencyCompact(summary.invoiced_month)}</span>
            </Tooltip>
          }
          hint="Month to date"
        />
        <DashboardTintedCard
          tone={summary.outstanding > 0 ? "amber" : "green"} icon={<WarningAmberIcon />}
          label="Outstanding"
          value={
            <Tooltip title={formatCurrency(summary.outstanding)} placement="top" arrow>
              <span>{isLoading ? <DashboardTintedSkeleton tone="amber" /> : formatCurrencyCompact(summary.outstanding)}</span>
            </Tooltip>
          }
          hint={summary.overdue_count > 0 ? `${summary.overdue_count} overdue` : "All settled"}
        />
        <DashboardTintedCard
          tone="teal" icon={<TrendingUpIcon />}
          label="Collection Rate"
          value={isLoading
            ? <DashboardTintedSkeleton tone="teal" />
            : (summary.collection_rate == null ? "—" : `${summary.collection_rate}%`)}
          hint={summary.collection_rate == null ? "No invoices this month" : "Paid vs invoiced (month)"}
        />
      </Box>

      {/* ══════════ Revenue by Location (admin all-locations only) ══════════ */}
      {isAdmin && locationId === "all" && locationRevenue.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: c.muted, textTransform: "uppercase", letterSpacing: "0.08em", mb: 1 }}>
            Revenue by Location · This Month
          </Typography>
          {/* Dynamic column count so 2 locations render as a clean
              2-across row instead of leaving 2 phantom empty tracks
              at md/lg (spec §2 / §7). */}
          <Box sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: `repeat(${Math.min(locationRevenue.length, 2)}, minmax(0, 1fr))`,
              md: `repeat(${Math.min(locationRevenue.length, 3)}, minmax(0, 1fr))`,
              lg: `repeat(${Math.min(locationRevenue.length, 4)}, minmax(0, 1fr))`,
            },
            gap: 1.5,
          }}>
            {locationRevenue.map((loc, idx) => {
              const rate = loc.collection_rate;
              // Stable tone rotation keyed by array index so a given
              // location keeps its colour across renders.
              const tone = toneForLocationIndex(idx);
              const t = toneMap[tone];
              return (
                <DashboardTintedCard
                  key={loc.location_id} tone={tone} interactive
                  onClick={() => setLocationId(loc.location_id)}
                  padding={1.75}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: t.accent }}>
                      {loc.location_name}
                    </Typography>
                    {rate != null
                      ? <Chip label={`${rate}%`} size="small" sx={{
                          height: 20, fontSize: 10, fontWeight: 700,
                          bgcolor: "#FFFFFFCC", color: t.accent,
                          border: `1px solid ${t.iconBg}44`,
                        }} />
                      : <Chip label="—" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "#FFFFFFCC", color: c.muted }} />}
                  </Box>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: t.accent, lineHeight: 1, letterSpacing: "-0.02em" }}>
                    {formatCurrency(loc.paid)}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: t.accent, opacity: 0.75, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", mt: 0.5 }}>
                    Paid
                  </Typography>
                  {rate != null && (
                    <Box sx={{ mt: 1, height: 5, bgcolor: "#FFFFFF88", borderRadius: 3, overflow: "hidden" }}>
                      <Box sx={{ height: "100%", width: `${Math.min(100, Math.max(0, rate))}%`, bgcolor: t.iconBg, transition: "width 400ms" }} />
                    </Box>
                  )}
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1, gap: 1 }}>
                    <Box>
                      <Typography sx={{ fontSize: 10, color: t.accent, opacity: 0.7, fontWeight: 700, textTransform: "uppercase" }}>Invoiced</Typography>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: t.accent }}>{formatCurrencyCompact(loc.invoiced)}</Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography sx={{ fontSize: 10, color: t.accent, opacity: 0.7, fontWeight: 700, textTransform: "uppercase" }}>Outstanding</Typography>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: loc.outstanding > 0 ? c.amber : t.accent }}>{formatCurrencyCompact(loc.outstanding)}</Typography>
                    </Box>
                  </Box>
                </DashboardTintedCard>
              );
            })}
          </Box>
        </Box>
      )}

      {/* ══════════ Recent Payments + Outstanding Invoices ══════════ */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        {/* Recent Payments — full solid emerald fill (spec §6). */}
        <Box sx={{ bgcolor: toneMap.emerald.grad, border: `1px solid ${toneMap.emerald.border}`, borderRadius: `${dashTokens.radius.md}px`, boxShadow: dashTokens.shadow.card, overflow: "hidden" }}>
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1, px: 2.5, py: 1.5,
            borderBottom: `1px solid ${c.border}`,
            background: toneMap.green.grad,
          }}>
            <Box sx={{ width: 30, height: 30, borderRadius: "10px", bgcolor: toneMap.green.iconBg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 17 }, boxShadow: `0 4px 12px ${toneMap.green.iconBg}40` }}>
              <PaymentsIcon />
            </Box>
            <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: toneMap.green.accent }}>Recent Payments</Typography>
            <Chip label={recentPayments.length} size="small" sx={{ ml: "auto", height: 22, fontSize: 11.5, fontWeight: 700, bgcolor: "#FFFFFFCC", color: toneMap.green.accent, border: `1px solid ${toneMap.green.iconBg}44` }} />
          </Box>
          <Box>
            {recentPayments.length === 0 ? (
              <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
                <Box sx={{ display: "inline-flex", width: 44, height: 44, borderRadius: "14px", bgcolor: toneMap.green.grad ? "#EAF8F3" : "", background: toneMap.green.grad, color: toneMap.green.accent, alignItems: "center", justifyContent: "center", mb: 1 }}>
                  <CheckCircleOutlineIcon />
                </Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: toneMap.green.accent }}>
                  No payments received today.
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: c.muted, mt: 0.5 }}>
                  Completed payments will appear here.
                </Typography>
              </Box>
            ) : recentPayments.map((p) => (
              <Box key={p.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 1.25, borderBottom: `1px solid ${c.border}`, "&:last-child": { borderBottom: "none" } }}>
                <Box sx={{ minWidth: 78, textAlign: "right", flexShrink: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: c.green, lineHeight: 1.1 }}>
                    {formatCurrency(p.amount)}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.student_name || "Unknown student"}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: c.muted }}>
                    {p.invoice_number ? `Invoice ${p.invoice_number} · ` : ""}
                    {p.method}
                    {" · "}{p.payment_date ? dayjs(p.payment_date).format("MMM D, h:mm A") : "—"}
                  </Typography>
                </Box>
                {p.invoice_id && (
                  <IconButton size="small" onClick={() => navigate(`/invoices/${p.invoice_id}`)}
                    sx={{ color: c.green }}>
                    <ArrowForwardIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Outstanding Invoices — full solid amber fill (spec §7). */}
        <Box sx={{ bgcolor: toneMap.amber.grad, border: `1px solid ${toneMap.amber.border}`, borderRadius: `${dashTokens.radius.md}px`, boxShadow: dashTokens.shadow.card, overflow: "hidden" }}>
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1, px: 2.5, py: 1.5,
            borderBottom: `1px solid ${c.border}`,
            background: toneMap.amber.grad,
          }}>
            <Box sx={{ width: 30, height: 30, borderRadius: "10px", bgcolor: toneMap.amber.iconBg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 17 }, boxShadow: `0 4px 12px ${toneMap.amber.iconBg}40` }}>
              <AccountBalanceWalletIcon />
            </Box>
            <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: toneMap.amber.accent }}>Outstanding Invoices</Typography>
            <Chip label={outstandingInvoices.length} size="small" sx={{ ml: "auto", height: 22, fontSize: 11.5, fontWeight: 700, bgcolor: "#FFFFFFCC", color: toneMap.amber.accent, border: `1px solid ${toneMap.amber.iconBg}44` }} />
          </Box>
          <Box>
            {outstandingInvoices.length === 0 ? (
              <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
                <Box sx={{ display: "inline-flex", width: 44, height: 44, borderRadius: "14px", background: toneMap.green.grad, color: toneMap.green.accent, alignItems: "center", justifyContent: "center", mb: 1 }}>
                  <CheckCircleOutlineIcon />
                </Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: toneMap.green.accent }}>
                  All invoices are settled.
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: c.muted, mt: 0.5 }}>
                  Nothing outstanding for the selected location.
                </Typography>
              </Box>
            ) : outstandingInvoices.map((i) => {
              const isOverdue = i.due_date && dayjs(i.due_date).isBefore(dayjs().startOf("day"));
              return (
                <Box key={i.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 1.25, borderBottom: `1px solid ${c.border}`, "&:last-child": { borderBottom: "none" } }}>
                  <Box sx={{ minWidth: 82, textAlign: "right", flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: isOverdue ? c.red : c.amber, lineHeight: 1.1 }}>
                      {formatCurrency(i.balance)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {i.student_name || "Unknown student"}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: c.muted }}>
                      {i.invoice_number || `INV #${i.id}`}
                      {" · "}
                      {i.due_date ? `Due ${dayjs(i.due_date).format("MMM D, YYYY")}` : "No due date"}
                      {isOverdue && (
                        <Box component="span" sx={{ color: c.red, fontWeight: 800, ml: 0.5 }}>· Overdue</Box>
                      )}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => navigate(`/invoices/${i.id}`)}
                    sx={{ color: c.amber }}>
                    <ArrowForwardIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* ── Finance action row (Aug 2026 polish) ──
          Two premium buttons matching the redesigned finance palette:
          View Invoices = blue secondary; Record Payment = green primary.
          Both preserve their existing routes and permissions. */}
      <Divider sx={{ mt: 3, mb: 2, borderColor: c.border }} />
      <Box>
        <Typography sx={{
          fontSize: 10, fontWeight: 700, color: c.muted,
          textTransform: "uppercase", letterSpacing: "0.1em", mb: 1,
        }}>
          Quick Finance Actions
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.25}
          sx={{ mb: 2 }}
        >
          <Button
            variant="outlined"
            startIcon={<ReceiptLongIcon sx={{ fontSize: "18px !important" }} />}
            onClick={() => navigate("/invoice/list")}
            sx={{
              width: { xs: "100%", sm: "auto" },
              height: 40, px: 2.25, borderRadius: "12px",
              textTransform: "none", fontSize: 13.5, fontWeight: 700,
              color: c.deepGreen,
              background: "#FFFFFF",
              border: `1px solid ${c.deepGreen}`,
              boxShadow: "none",
              transition: "all 180ms ease",
              "&:hover": {
                background: "#F2F6FA",
                borderColor: c.deepGreen,
                transform: "translateY(-1px)",
                boxShadow: "0 6px 16px rgba(23,50,77,0.08)",
              },
              "&:active": { transform: "translateY(0)" },
            }}
          >
            View Invoices
          </Button>
          <Button
            variant="contained"
            startIcon={<PaymentsIcon sx={{ fontSize: "18px !important" }} />}
            onClick={() => navigate("/payment/create")}
            sx={{
              width: { xs: "100%", sm: "auto" },
              height: 40, px: 2.25, borderRadius: "12px",
              textTransform: "none", fontSize: 13.5, fontWeight: 700,
              color: "#FFFFFF",
              background: "#5BAA83",
              boxShadow: "0 4px 12px rgba(75,140,105,0.24)",
              transition: "all 180ms ease",
              "&:hover": {
                background: "#4F9271",
                transform: "translateY(-1px)",
                boxShadow: "0 6px 16px rgba(75,140,105,0.32)",
              },
              "&:active": { transform: "translateY(0)" },
            }}
          >
            Record Payment
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default FinanceOverview;
